import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { FieldMetaProps, FormikProps, FieldInputProps } from 'formik';
import classNames from 'classnames';
import { Eye } from 'react-feather';

import TooltipBox from './Overlay/TooltipBox';
import ErrorFeedback from '../ErrorFeedback';

export interface BaseInputTextProps {
  label?: string;
  trimmed?: boolean;
  type?: 'text' | 'password' | 'email' | 'number' | string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  autoFocus?: boolean;
  autoComplete?: string;
  id?: string;
  inputId?: string;
  inputClassName?: string;
  containerClassName?: string;
  labelClassName?: string;
  iconWrapperClassName?: string; // For the div wrapping input and eye icon
  size?: 'sm' | 'lg';
  ariaLabel?: string;
  ariaDescribedBy?: string;
  name?: string;
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error?: string;
  touched?: boolean;
  formGroup?: FormikProps<any>;
}

const BaseInputText: React.FC<BaseInputTextProps> = (props) => {
  const {
    label,
    trimmed = false,
    type = 'text',
    placeholder,
    disabled = false,
    readOnly = false,
    maxLength,
    autoFocus = false,
    autoComplete,
    id,
    inputId,
    inputClassName,
    containerClassName,
    labelClassName,
    iconWrapperClassName = '',
    size = 'lg',
    ariaLabel,
    ariaDescribedBy,
    name,
    formGroup,
  } = props;
  const field: FieldInputProps<any> | null = formGroup && name ? formGroup?.getFieldProps(name) : null;
  const meta: FieldMetaProps<unknown> | null = formGroup && name ? formGroup?.getFieldMeta(name) : null;

  const isActuallyFormikConnected = !!field && !!meta;

  const [showFullText, setShowFullText] = useState(false);

  const effectiveValue = field ? field.value : (props.value ?? '');
  const effectiveError = meta ? meta.error : props.error;
  const effectiveTouched = meta ? meta.touched : (props.touched ?? false);

  const isInvalid = !!(effectiveTouched && effectiveError);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (field && field.onChange) {
      field.onChange(e);
    }

    if (props.onChange) {
      props.onChange(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (isActuallyFormikConnected && field.onBlur) {
      field.onBlur(e);
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const baseInputId = inputId || (name ? `base-input-${name}` : undefined);
  const errorId = baseInputId && isInvalid ? `${baseInputId}-error` : undefined;
  const describedBy = classNames(ariaDescribedBy, errorId).trim() || undefined;

  const formControl = (
    <Form.Control
      id={baseInputId}
      type={showFullText ? 'text' : type}
      placeholder={placeholder}
      value={effectiveValue}
      onChange={handleChange}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={readOnly}
      maxLength={maxLength}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      className={classNames(inputClassName)}
      size={size}
      isInvalid={isInvalid}
      aria-label={ariaLabel || label}
      aria-describedby={describedBy}
      aria-invalid={isInvalid}
    />
  );

  const metaForErrorFeedback: FieldMetaProps<any> =
    isActuallyFormikConnected && meta
      ? meta
      : {
          value: props.value ?? '',
          error: props.error,
          touched: props.touched ?? false,
          initialValue: undefined,
          initialTouched: false,
          initialError: undefined,
        };

  return (
    <div className={containerClassName} id={id}>
      {label && <Form.Label className={`fs-name mb-0 ${labelClassName}`}>{label}</Form.Label>}
      {trimmed ? (
        <div className={classNames(`role-input-icon-wrapper ${iconWrapperClassName}`, { 'is-invalid': isInvalid })}>
          {formControl}
          <span className="role-icon-suffix">
            {' '}
            {/* Default class from example */}
            <TooltipBox
              msg={String(effectiveValue)}
              placement="top"
              trigger={['click']}
              closeOnOutsideClick={true}
              hideDelay={0}
              showDelay={0}
              onVisibleChange={setShowFullText} // Toggle showFullText state
            >
              <span className="role-icon-eye">
                {' '}
                {/* Default class from example */}
                <Eye aria-label={showFullText ? 'Hide full text' : 'Show full text'} />
              </span>
            </TooltipBox>
          </span>
        </div>
      ) : (
        formControl
      )}
      {isInvalid && effectiveError && <ErrorFeedback field={metaForErrorFeedback} />}
    </div>
  );
};

export default BaseInputText;
